import startInDomain from "@ext/markdown/elements/list/edit/models/orderList/logic/startInDomain";
import type { JSONContent } from "@tiptap/core";

type HtmlTagToComponentType = (node: JSONContent) => JSONContent;

const headingTransformer =
	(level: number): HtmlTagToComponentType =>
	(node) => {
		node.type = "heading";
		node.attrs = { ...node.attrs?.attributes, level };
		return node;
	};

const markTransformer =
	(markName: string, getAttrs?: (node: JSONContent) => Record<string, unknown>): HtmlTagToComponentType =>
	(node) => {
		// What the tag carried, kept on the mark: an `<a>` without its address is a link to nowhere.
		const attrs = getAttrs?.(node);
		const mark = attrs ? { type: markName, attrs } : { type: markName };

		const traverse = (node: JSONContent) => {
			if (node.type === "blockHtmlTagComponent" || node.type === "inlineHtmlTagComponent") {
				return node.attrs.content.content.map(traverse);
			}
			node.marks = [...(node.marks || []), { ...mark }];
		};

		node.content?.map(traverse);
		return node.content;
	};

const NodeTransformer =
	(nodeName: string, getAttrs?: (node: JSONContent) => Record<string, unknown>): HtmlTagToComponentType =>
	(node) => {
		node.type = nodeName;
		node.attrs = {
			...node.attrs?.attributes,
			...(getAttrs ? getAttrs(node) : {}),
		};
		return node;
	};

const removeNode: HtmlTagToComponentType = (node) => {
	return node.content;
};

const HtmlTagToComponent = {
	p: removeNode,
	div: removeNode,
	span: removeNode,
	b: markTransformer("strong"),
	strong: markTransformer("strong"),
	i: markTransformer("em"),
	em: markTransformer("em"),
	// The address is kept as written; what it points at inside the catalog is resolved on the next
	// read, when the link goes through the parser as Markdown of our own.
	a: markTransformer("link", (node) => ({
		href: node.attrs?.attributes?.href ?? "",
		resourcePath: "",
		hash: "",
		isFile: false,
		title: node.attrs?.attributes?.title ?? null,
	})),
	code: markTransformer("code"),
	strike: markTransformer("s"),
	s: markTransformer("s"),
	h1: headingTransformer(2),
	h2: headingTransformer(2),
	h3: headingTransformer(3),
	h4: headingTransformer(4),
	h5: headingTransformer(5),
	h6: headingTransformer(6),
	pre: NodeTransformer("code_block"),
	li: NodeTransformer("listItem"),
	ul: NodeTransformer("bulletList"),
	ol: NodeTransformer("orderedList", (node) => ({ start: startInDomain(node.attrs?.attributes?.start) })),
	blockquote: NodeTransformer("note", () => ({ type: "quote" as never })),
	img: NodeTransformer("image"),
} as const;

export default HtmlTagToComponent;
